import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Bocadillo } from '#components/mobile/travesia/Bocadillo';
import { OPCIONES_VIVE } from '#components/mobile/travesia/catalogo';
import type { Perfil, Vive } from '#components/mobile/travesia/catalogo';
import type { GaviEstado } from '#components/mobile/travesia/Gavi';
import { Boton } from '#components/mobile/ui/Boton';
import type { NombreIcono } from '#components/mobile/ui/Icono';
import { IconoCaja } from '#components/mobile/ui/IconoCaja';
import { colorCategoria } from '#components/mobile/ui/tokens';

import {
  estiloOpcion,
  estiloOpciones,
  HojaTravesia,
  Interruptor,
  Marca,
  TituloHoja,
} from './comun';

/** Trazos que no están en `Icono` (llave, huella, bebé), de la maqueta. */
const TRAZOS_PROPIOS = {
  llave: (
    <>
      <circle cx="8" cy="15" r="4" />
      <path d="M11 12l9-9M17 6l2 2M15 8l2 2" />
    </>
  ),
  huella: (
    <>
      <circle cx="7" cy="10" r="1.8" />
      <circle cx="17" cy="10" r="1.8" />
      <circle cx="10" cy="6" r="1.8" />
      <circle cx="14" cy="6" r="1.8" />
      <path d="M8 17c0-3 2-5 4-5s4 2 4 5c0 2-2 2-4 1.5C10 19 8 19 8 17z" />
    </>
  ),
  bebe: (
    <>
      <circle cx="12" cy="7" r="3.5" />
      <path d="M6 21v-4a6 6 0 0 1 12 0v4" />
    </>
  ),
};

function IconoPropio({ nombre }: { nombre: keyof typeof TRAZOS_PROPIOS }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={18}
      height={18}
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      {TRAZOS_PROPIOS[nombre]}
    </svg>
  );
}

const ICONO_VIVE: Record<Vive, NombreIcono | 'llave'> = {
  familia: 'home',
  sin: 'user',
  pareja: 'users',
  piso: 'llave',
};

type HojaViveProps = {
  perfil: Perfil;
  ocupado?: boolean;
  onContinuar: (perfil: Perfil) => void;
};

/**
 * Paso 2 · «¿Con quién vives?» sobre el Plan vacío. No escribe nada todavía:
 * decide qué cofres salen ya marcados en los pasos 4 y 5.
 */
export function HojaVive({ perfil, ocupado, onContinuar }: HojaViveProps) {
  const { t } = useTranslation();
  const [p, setP] = useState<Perfil>(perfil);
  const [gavi, setGavi] = useState<{
    texto: string | null;
    estado: GaviEstado;
    clave: number;
  }>({ texto: null, estado: 'vuela', clave: 0 });

  const textoVive: Record<Vive, string> = {
    familia: t('With my family'),
    sin: t('On my own'),
    pareja: t('With my partner'),
    piso: t('Shared flat'),
  };

  function alternar(k: 'mascota' | 'hijos') {
    const valor = !p[k];
    setP({ ...p, [k]: valor });
    setGavi(g => ({
      texto: valor
        ? k === 'mascota'
          ? t('A pet? If it is a cat, I will keep my distance.')
          : t('Little ones on board: I will add a category for their things.')
        : null,
      estado: valor ? (k === 'mascota' ? 'eriza' : 'aplaude') : 'reposo',
      clave: g.clave + 1,
    }));
  }

  return (
    <HojaTravesia
      aria-label={t('Who do you live with?')}
      data-testid="hoja-vive"
      cabeza={
        <Bocadillo
          variante="hoja"
          texto={
            gavi.texto ??
            t(
              'This is not gossip: without it I might suggest a rent you may not pay.',
            )
          }
          estado={gavi.estado}
          clave={gavi.clave}
        />
      }
      pie={
        <Boton
          bloque
          isDisabled={ocupado}
          data-testid="travesia-continuar"
          onPress={() => onContinuar(p)}
        >
          <Trans>Continue</Trans>
        </Boton>
      }
    >
      <TituloHoja>
        <Trans>Who do you live with?</Trans>
      </TituloHoja>
      <div style={estiloOpciones}>
        {OPCIONES_VIVE.map((v, i) => {
          const icono = ICONO_VIVE[v];
          return (
            <button
              type="button"
              key={v}
              aria-pressed={p.vive === v}
              data-testid={`vive-${v}`}
              style={estiloOpcion(i === 0)}
              onClick={() => {
                setP({ ...p, vive: v });
                setGavi(g => ({
                  texto: null,
                  estado: 'reposo',
                  clave: g.clave,
                }));
              }}
            >
              {icono === 'llave' ? (
                <IconoCaja tono="acento" size={36}>
                  <IconoPropio nombre="llave" />
                </IconoCaja>
              ) : (
                <IconoCaja tono="acento" size={36} icono={icono} />
              )}
              <span style={{ flex: 1, minWidth: 0, lineHeight: 1.25 }}>
                {textoVive[v]}
              </span>
              <Marca marcada={p.vive === v} />
            </button>
          );
        })}
      </div>
      <div style={{ ...estiloOpciones, marginTop: 10 }}>
        {(['mascota', 'hijos'] as const).map((k, i) => (
          <button
            type="button"
            key={k}
            role="switch"
            aria-checked={p[k]}
            data-testid={`vive-${k}`}
            style={estiloOpcion(i === 0)}
            onClick={() => alternar(k)}
          >
            <IconoCaja
              size={36}
              colorPropio={colorCategoria(k === 'mascota' ? 7 : 6)}
            >
              <IconoPropio nombre={k === 'mascota' ? 'huella' : 'bebe'} />
            </IconoCaja>
            <span style={{ flex: 1, minWidth: 0, lineHeight: 1.25 }}>
              {k === 'mascota' ? t('I have a pet') : t('I have children')}
            </span>
            <Interruptor encendido={p[k]} />
          </button>
        ))}
      </div>
    </HojaTravesia>
  );
}
